import { useState, useRef, useEffect, type FormEvent, type ChangeEvent } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBriefcase,
  faUserPlus,
  faPlusCircle,
  faTag,
  faLayerGroup,
  faBuilding,
  faUser,
  faPlus,
  faUndo,
  faSpinner,
  faEnvelope,
  faPhone,
  faMapMarkerAlt,
  faMailBulk,
  faCalendarAlt,
} from '@fortawesome/free-solid-svg-icons';
import {
  usePipelines,
  useDefendantSearch,
  useContactSearch,
  useCreateCase,
  useCreateContact,
} from '../../hooks/useNewRecord';
import { useToast } from '../../hooks/useToast';
import { usePermissions } from '../../hooks/usePermissions';
import type { PipelineStage } from '../../types/newRecord';
import css from './NewRecordPage.module.css';

/* ────────────────────────────────────────────────────────────
 * Case types that do not require a lender
 * ──────────────────────────────────────────────────────────── */
const NO_LENDER_TYPES = new Set(['Wills', 'Debt Collections', 'Pensions', 'Test Pipeline', 'test']);

/* ────────────────────────────────────────────────────────────
 * AsyncCombobox — reusable searchable dropdown
 * ──────────────────────────────────────────────────────────── */
interface ComboOption {
  value: string;
  label: string;
}

interface AsyncComboboxProps {
  options: ComboOption[];
  loading: boolean;
  value: string;
  placeholder: string;
  onSearch: (term: string) => void;
  onChange: (value: string) => void;
  required?: boolean;
  id?: string;
}

function AsyncCombobox({
  options,
  loading,
  value,
  placeholder,
  onSearch,
  onChange,
  required,
  id,
}: AsyncComboboxProps) {
  const [open, setOpen] = useState(false);
  const [inputVal, setInputVal] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const selectedLabel = options.find((o) => o.value === value)?.label ?? '';

  return (
    <div className={css.comboWrapper} ref={wrapperRef}>
      <input
        id={id}
        className={css.comboInput}
        placeholder={value ? selectedLabel : placeholder}
        value={open ? inputVal : value ? selectedLabel : ''}
        required={required && !value}
        onFocus={() => {
          setOpen(true);
          setInputVal('');
        }}
        onChange={(e) => {
          setInputVal(e.target.value);
          onSearch(e.target.value);
        }}
        autoComplete="off"
      />
      {value && (
        <button
          type="button"
          className={css.comboClear}
          onClick={() => {
            onChange('');
            setInputVal('');
          }}
          aria-label="Clear"
        >
          ×
        </button>
      )}
      {open && (
        <ul className={css.comboDropdown}>
          {loading && (
            <li className={css.comboLoading}>
              <FontAwesomeIcon icon={faSpinner} spin /> Searching…
            </li>
          )}
          {!loading && options.length === 0 && inputVal.length >= 2 && (
            <li className={css.comboEmpty}>No results found</li>
          )}
          {!loading && inputVal.length < 2 && (
            <li className={css.comboEmpty}>Type at least 2 characters…</li>
          )}
          {options.map((opt) => (
            <li
              key={opt.value}
              className={css.comboOption}
              onMouseDown={(e) => {
                e.preventDefault();
                onChange(opt.value);
                setInputVal('');
                setOpen(false);
              }}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
 * NewCaseForm
 * ──────────────────────────────────────────────────────────── */

function NewCaseForm() {
  const { showToast } = useToast();
  const { data: pipelines = [], isLoading: pipelinesLoading } = usePipelines();
  const lenderSearch = useDefendantSearch();
  const contactSearch = useContactSearch();
  const createCase = useCreateCase();

  const [caseType, setCaseType] = useState('');
  const [stageId, setStageId] = useState('');
  const [lender, setLender] = useState('');
  const [contactId, setContactId] = useState('');

  // Derived
  const activePipelines = pipelines.filter((p) => p.is_active);
  const selectedPipeline = pipelines.find(
    (p) => p.name === caseType || p.display_name === caseType,
  );
  const stages: PipelineStage[] = selectedPipeline?.stages.filter((s) => s.is_active) ?? [];
  const needsLender = caseType !== '' && !NO_LENDER_TYPES.has(caseType);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!caseType || !contactId) {
      showToast('Case Type and Client Contact are required.', 'error');
      return;
    }
    if (needsLender && !lender) {
      showToast('Defendant is required for this case type.', 'error');
      return;
    }
    try {
      const result = await createCase.mutateAsync({
        caseType,
        lender: lender || null,
        ContactID: contactId,
        stageId: stageId ? parseInt(stageId, 10) : null,
      });
      showToast(
        `Case created successfully! Reference: ${result.caseRef ?? result.CaseRef}`,
        'success',
      );
      // Reset
      setCaseType('');
      setStageId('');
      setLender('');
      setContactId('');
      lenderSearch.setResults([]);
      contactSearch.setResults([]);
    } catch (err) {
      showToast(
        `Error creating case: ${err instanceof Error ? err.message : String(err)}`,
        'error',
      );
    }
  };

  const handleReset = () => {
    setCaseType('');
    setStageId('');
    setLender('');
    setContactId('');
    lenderSearch.setResults([]);
    contactSearch.setResults([]);
  };

  return (
    <form className={css.form} onSubmit={handleSubmit}>
      <div className={css.formSection}>
        {/* Case Type */}
        <div className={css.formGroup}>
          <label htmlFor="case-type" className={css.formLabel}>
            <FontAwesomeIcon icon={faTag} className={css.labelIcon} />
            Case Type
          </label>
          <select
            id="case-type"
            className={css.formControl}
            required
            value={caseType}
            onChange={(e) => {
              setCaseType(e.target.value);
              setStageId('');
            }}
            disabled={pipelinesLoading}
          >
            <option value="">
              {pipelinesLoading ? 'Loading case types…' : 'Select case type…'}
            </option>
            {activePipelines.map((p) => (
              <option key={p.id} value={p.name}>
                {p.display_name}
              </option>
            ))}
          </select>
        </div>

        {/* Initial Stage (shown when case type selected) */}
        {caseType && (
          <div className={css.formGroup}>
            <label htmlFor="initial-stage" className={css.formLabel}>
              <FontAwesomeIcon icon={faLayerGroup} className={css.labelIcon} />
              Initial Stage
            </label>
            <select
              id="initial-stage"
              className={css.formControl}
              value={stageId}
              onChange={(e) => setStageId(e.target.value)}
            >
              <option value="">Default stage</option>
              {stages.map((s) => (
                <option key={s.stage_id} value={s.stage_id}>
                  {s.stage_name}
                </option>
              ))}
            </select>
            <small className={css.formHint}>Leave as default or choose a specific stage</small>
          </div>
        )}

        {/* Defendant (hidden for Wills etc.) */}
        {needsLender && (
          <div className={css.formGroup}>
            <label htmlFor="defendant" className={css.formLabel}>
              <FontAwesomeIcon icon={faBuilding} className={css.labelIcon} />
              Defendant
            </label>
            <AsyncCombobox
              id="defendant"
              options={lenderSearch.results}
              loading={lenderSearch.loading}
              value={lender}
              placeholder="Search for defendant…"
              onSearch={lenderSearch.search}
              onChange={setLender}
              required
            />
          </div>
        )}

        {/* Client Contact */}
        <div className={css.formGroup}>
          <label htmlFor="client-contact" className={css.formLabel}>
            <FontAwesomeIcon icon={faUser} className={css.labelIcon} />
            Client Contact
          </label>
          <AsyncCombobox
            id="client-contact"
            options={contactSearch.results}
            loading={contactSearch.loading}
            value={contactId}
            placeholder="Search for client contact…"
            onSearch={contactSearch.search}
            onChange={setContactId}
            required
          />
        </div>
      </div>

      <div className={css.formActions}>
        <button
          type="submit"
          className={`${css.btn} ${css.btnPrimary}`}
          disabled={createCase.isPending}
        >
          {createCase.isPending ? (
            <>
              <FontAwesomeIcon icon={faSpinner} spin /> Creating…
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faPlus} /> Create Case
            </>
          )}
        </button>
        <button type="button" className={`${css.btn} ${css.btnSecondary}`} onClick={handleReset}>
          <FontAwesomeIcon icon={faUndo} /> Reset
        </button>
      </div>
    </form>
  );
}

/* ────────────────────────────────────────────────────────────
 * NewContactForm
 * ──────────────────────────────────────────────────────────── */

function NewContactForm() {
  const { showToast } = useToast();
  const createContact = useCreateContact();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [postcode, setPostcode] = useState('');
  const [dob, setDob] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim()) {
      showToast('First name and last name are required.', 'error');
      return;
    }
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      showToast('Please enter a valid email address.', 'error');
      return;
    }
    try {
      const result = await createContact.mutateAsync({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim() || null,
        phone: phone.trim() || null,
        address: address.trim() || null,
        postcode: postcode.trim() || null,
        dob: dob || null,
      });
      if (result.success === false && result.error) {
        showToast(result.error, 'error');
        return;
      }
      showToast(`Contact created successfully! Contact ID: ${result.contactId}`, 'success');
      setFirstName('');
      setLastName('');
      setEmail('');
      setPhone('');
      setAddress('');
      setPostcode('');
      setDob('');
    } catch (err: unknown) {
      const axErr = err as { response?: { data?: { error?: string } } };
      const serverMsg = axErr?.response?.data?.error;
      const msg = serverMsg || (err instanceof Error ? err.message : String(err));
      showToast(`Error creating contact: ${msg}`, 'error');
    }
  };

  const handleReset = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setAddress('');
    setPostcode('');
    setDob('');
  };

  const field = (
    id: string,
    label: string,
    icon: typeof faUser,
    value: string,
    setter: (v: string) => void,
    opts?: { type?: string; required?: boolean; placeholder?: string },
  ) => (
    <div className={css.formGroup}>
      <label htmlFor={id} className={css.formLabel}>
        <FontAwesomeIcon icon={icon} className={css.labelIcon} />
        {label}
        {opts?.required && ' *'}
      </label>
      <input
        id={id}
        type={opts?.type ?? 'text'}
        className={css.formControl}
        value={value}
        onChange={(e: ChangeEvent<HTMLInputElement>) => setter(e.target.value)}
        required={opts?.required}
        placeholder={opts?.placeholder ?? `Enter ${label.toLowerCase()}`}
      />
    </div>
  );

  return (
    <form className={css.form} onSubmit={handleSubmit}>
      <div className={css.formSection}>
        {field('contact-first-name', 'First Name', faUser, firstName, setFirstName, {
          required: true,
        })}
        {field('contact-last-name', 'Last Name', faUser, lastName, setLastName, {
          required: true,
        })}
        {field('contact-email', 'Email', faEnvelope, email, setEmail, {
          type: 'email',
          placeholder: 'Enter email address',
        })}
        {field('contact-phone', 'Phone Number', faPhone, phone, setPhone, {
          type: 'tel',
          placeholder: 'Enter phone number',
        })}
        {field('contact-address', 'Address', faMapMarkerAlt, address, setAddress, {
          placeholder: 'Enter address (street, city, county)',
        })}
        {field('contact-postcode', 'Postcode', faMailBulk, postcode, setPostcode, {
          placeholder: 'Enter postcode',
        })}
        {field('contact-dob', 'Date of Birth', faCalendarAlt, dob, setDob, {
          type: 'date',
        })}
      </div>

      <div className={css.formActions}>
        <button
          type="submit"
          className={`${css.btn} ${css.btnPrimary}`}
          disabled={createContact.isPending}
        >
          {createContact.isPending ? (
            <>
              <FontAwesomeIcon icon={faSpinner} spin /> Creating…
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faUserPlus} /> Create Contact
            </>
          )}
        </button>
        <button type="button" className={`${css.btn} ${css.btnSecondary}`} onClick={handleReset}>
          <FontAwesomeIcon icon={faUndo} /> Reset
        </button>
      </div>
    </form>
  );
}

/* ────────────────────────────────────────────────────────────
 * Main Page
 * ──────────────────────────────────────────────────────────── */

type Tab = 'case' | 'contact';

export default function NewRecordPage() {
  const { isSuperAdmin, hasPermission } = usePermissions();
  const canCreateCases = isSuperAdmin || hasPermission('create_cases');
  const canCreateContacts = isSuperAdmin || hasPermission('create_contacts');

  const defaultTab: Tab = canCreateCases ? 'case' : 'contact';
  const [tab, setTab] = useState<Tab>(defaultTab);

  return (
    <div className={css.wrapper}>
      {/* Page header */}
      <div className={css.pageHeader}>
        <h2 className={css.pageTitle}>Create New Record</h2>
        <p className={css.pageSubtitle}>
          Enter details to create a new case or contact in the system
        </p>
      </div>

      {/* Card */}
      <div className={css.card}>
        <div className={css.cardHeader}>
          <h3 className={css.cardTitle}>
            <FontAwesomeIcon icon={faPlusCircle} />
            New Record
          </h3>
        </div>

        <div className={css.cardContent}>
          {/* Tabs */}
          <div className={css.tabNav}>
            {canCreateCases && (
              <button
                type="button"
                className={`${css.tabBtn} ${tab === 'case' ? css.tabActive : ''}`}
                onClick={() => setTab('case')}
              >
                <FontAwesomeIcon icon={faBriefcase} /> New Case
              </button>
            )}
            {canCreateContacts && (
              <button
                type="button"
                className={`${css.tabBtn} ${tab === 'contact' ? css.tabActive : ''}`}
                onClick={() => setTab('contact')}
              >
                <FontAwesomeIcon icon={faUserPlus} /> New Contact
              </button>
            )}
          </div>

          {/* Tab content */}
          <div className={css.tabContent}>
            {tab === 'case' ? <NewCaseForm /> : <NewContactForm />}
          </div>
        </div>
      </div>
    </div>
  );
}
